import * as React from 'react';;
import axios from '../../axios-config';
import { Organization } from '../../models/organization';

interface OrganizationFormProps {
  organizationType: number;
  newOrganization: Partial<Organization>;
  setNewOrganization: (org: Partial<Organization>) => void;
  isEditMode?: boolean; // flag to determine if we're editing or creating
  onClose: () => void;
  onSave?: (savedOrg: Organization) => void; // new callback prop
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({
  organizationType,
  newOrganization,
  setNewOrganization,
  isEditMode = false,
  onClose,
  onSave
}) => {

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewOrganization({
      ...newOrganization,
      [name]: name === 'pointsAvailable' ? Number(value) : value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Assumes newOrganization.organizationId exists in edit mode.
    const url = isEditMode && newOrganization.organizationId 
      ? `/api/organizations/${newOrganization.organizationId}` 
      : `/api/organizations`;
    try {
      let response;
      if (isEditMode) {
        response = await axios.put(url, newOrganization);
      } else {
        newOrganization.organizationTypeId = organizationType;
        response = await axios.post(url, newOrganization);
      }
      // Call onSave with returned record
      if (onSave && response?.data) {
        onSave(response.data);
      }
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <div className="bg-white p-4 rounded shadow mb-6">
      <h2 className="text-xl font-semibold mb-4">{isEditMode ? 'Edit Organization' : 'New Organization'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-1" htmlFor="organizationName">Organization Name</label>
          <input
            type="text"
            id="organizationName"
            name="organizationName"
            placeholder="Organization Name"
            value={newOrganization.organizationName || ''}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-1" htmlFor="pointsAvailable">Funds Available</label>
          <input
            type="number"
            id="pointsAvailable"
            name="pointsAvailable"
            placeholder="Funds Available"
            value={newOrganization.pointsAvailable || 0}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-1" htmlFor="mascotName">Mascot Name</label>
          <input
            type="text"
            id="mascotName"
            name="mascotName"
            placeholder="Mascot Name"
            value={newOrganization.mascotName || ''}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-1" htmlFor="organizationSlug">Organization Slug</label>
          <input
            type="text"
            id="organizationSlug"
            name="organizationSlug"
            placeholder="Organization Slug"
            value={newOrganization.organizationSlug || ''}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-1" htmlFor="icon">Icon</label>
          <input
            type="text"
            id="icon"
            name="icon"
            placeholder="Icon (e.g., emoji or icon name)"
            value={newOrganization.icon || ''}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-1" htmlFor="color">Color</label>
          <input
            type="text"
            id="color"
            name="color"
            placeholder="Color (hex code or color name)"
            value={newOrganization.color || ''}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-1" htmlFor="accent">Accent Color</label>
          <input
            type="text"
            id="accent"
            name="accent"
            placeholder="Accent Color (hex code or color name)"
            value={newOrganization.accent || ''}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-1" htmlFor="gemGradient">Gem Gradient</label>
          <input
            type="text"
            id="gemGradient"
            name="gemGradient"
            placeholder="Gem Gradient (CSS gradient)"
            value={newOrganization.gemGradient || ''}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-1" htmlFor="backgroundImageUrl">Background Image URL</label>
          <input
            type="text"
            id="backgroundImageUrl"
            name="backgroundImageUrl"
            placeholder="Background Image URL"
            value={newOrganization.backgroundImageUrl || ''}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="flex justify-end gap-4">
          <button
            type="submit"
            className="bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-2 rounded"
          >
            {isEditMode ? 'Save Organization' : 'Create Organization'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-300 text-black rounded px-4 py-2"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrganizationForm;
